/**
 * Work history: what each role was responsible for — nothing more. No commit
 * logs, hashes or branch names: a reader can't tell a decorative commit from a
 * real one, and real ones live in private company repos.
 *
 * One JSON file per role in src/content/experience, edited at /admin.
 */
import { readFolder } from "../content/load";

export interface YearMonth {
    year: number;
    /** 1–12 */
    month: number;
}

export interface Experience {
    title: string;
    company: string;
    period: string;
    start: YearMonth;
    end: YearMonth;
    /** One or two words for the role's focus, shown on the card's header. */
    focus: string;
    description: string;
    responsibilities: string[];
}

interface ExperienceFile {
    title: string;
    company: string;
    focus?: string;
    /** "YYYY-MM" */
    start: string;
    /** "YYYY-MM", or empty while the role is ongoing. */
    end?: string;
    description?: string;
    responsibilities?: string[];
}

function parseMonth(s: string | undefined): YearMonth | null {
    const m = /^(\d{4})-(\d{1,2})$/.exec((s ?? "").trim());
    return m ? { year: Number(m[1]), month: Number(m[2]) } : null;
}

const now = new Date();
const thisMonth: YearMonth = { year: now.getFullYear(), month: now.getMonth() + 1 };
const fmt = (d: YearMonth) => `${String(d.month).padStart(2, "0")}/${d.year}`;
const index = (d: YearMonth) => d.year * 12 + d.month;

/** Oldest first: the Experience scene walks its timeline left to right. */
export const EXPERIENCES: Experience[] = readFolder<ExperienceFile>(
    require.context("../content/experience", false, /\.json$/)
)
    .map(({ data }) => {
        const start = parseMonth(data.start) ?? thisMonth;
        const end = parseMonth(data.end);
        return {
            title: data.title,
            company: data.company,
            focus: data.focus ?? "",
            description: data.description ?? "",
            responsibilities: data.responsibilities ?? [],
            start,
            end: end ?? thisMonth,
            period: `${fmt(start)} — ${end ? fmt(end) : "Present"}`,
        };
    })
    .sort((a, b) => index(a.start) - index(b.start));
